import { FC } from 'react'

import {
  AccountingDraftEntriesWidgetDocument,
  AccountingDraftEntriesWidgetQuery,
  FinanceJournalDraftEntryFilter,
  FinanceJournalEntryFilter,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  displayedFilters: (string | string[])[]
}

interface QueryArgsType {
  companyId: string
  filter?: FinanceJournalDraftEntryFilter
  filterEntry?: FinanceJournalEntryFilter
}

const prepareData = (
  data: AccountingDraftEntriesWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const countPublished = data?.finance?.countPublished.totalCount || 0
  const countReadyToRecord = data?.finance?.countReadyToPublish.totalCount || 0
  const countUnbalanced = data?.finance?.countUnbalanced.totalCount || 0
  const countDraft = data?.finance?.countDraft.totalCount || 0
  const totalCount = countReadyToRecord + countUnbalanced + countDraft

  const overviewChartData: ChartData = {
    label: i18n.t('finance.overview.outstanding_payments.total'),
    value: totalCount,
    formattedValue: i18n.formatNumber(totalCount),
  }

  const countersChartDatas: ChartData[] = [
    {
      label: i18n.t('finance.overview.accounting.published'),
      value: countPublished,
      formattedValue: i18n.formatNumber(countPublished),
    },
    {
      label: i18n.t('finance.overview.accounting.pending_publish'),
      value: totalCount,
      formattedValue: i18n.formatNumber(totalCount),
    },
  ]

  const chartDatas: ChartData[] = [
    {
      status: 'ready_to_publish',
      color: 'feedback-positive',
      label: i18n.t('finance.journal_entry.data.status.ready_to_publish'),
      value: countReadyToRecord,
      formattedValue: i18n.formatNumber(countReadyToRecord),
    },
    {
      status: 'unbalanced',
      color: 'feedback-negative',
      label: i18n.t('finance.journal_entry.data.status.unbalanced'),
      value: countUnbalanced,
      formattedValue: i18n.formatNumber(countUnbalanced),
    },
    {
      status: 'draft',
      color: 'feedback-neutral',
      label: i18n.t('finance.journal_entry.data.status.draft'),
      value: countDraft,
      formattedValue: i18n.formatNumber(countDraft),
    },
  ]

  return {
    countersChartDatas,
    overviewChartData,
    chartDatas,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildAccountingDraftEntriesDashboardWidget = ({
  queryArgs,
  prepareDataArgs,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
}) => {
  const AccountingDraftEntriesDashboardWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { navigation } = useNavigation()
    const { overviewChartData, chartDatas, displayedFilters, countersChartDatas } = data

    const { filter } = queryArgs

    const [from, to] = filter?.referenceDate?.between || []

    let dateQueryString = ''
    if (from && to) {
      dateQueryString = `referenceDateFrom=${from}&referenceDateTo=${to}`
    }

    return (
      <FinanceWidget
        id="accountingDraftEntriesDashboard"
        title={i18n.t('finance.overview.accounting.title_v2')}
        displayedFilters={displayedFilters}
        type={SupportedCharts.PIE}
        showChart
        chartDatas={chartDatas}
        countersChartDatas={countersChartDatas}
        overviewChartData={overviewChartData}
        linkData={{
          url: `${navigation.finance.accounting.draftEntries.fullPath()}?${dateQueryString}`,
          title: i18n.t('finance.overview.accounting.link_title'),
        }}
        emptyState={{
          icon: '☕',
          text: i18n.t('finance.overview.accounting.empty_state.title'),
          description: i18n.t('finance.overview.accounting.empty_state.description'),
        }}
      />
    )
  }

  return buildWidget({
    id: 'accountingDraftEntriesWidget',
    component: AccountingDraftEntriesDashboardWidget,
    document: AccountingDraftEntriesWidgetDocument,
    prepareData: (data: AccountingDraftEntriesWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: queryArgs,
  })
}
